import { useEffect, useState } from "react";
import { Printer, Eye } from "lucide-react";
import api, { API, mediaUrl, apiErr, SITE_URL, qrTrackUrl } from "@/lib/api";
import Modal from "@/components/admin/Modal";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUSES = ["pending", "paid", "shipped", "delivered", "cancelled"];
const FULFILL = ["pending", "shipped", "delivered"];

export default function AdminOrders() {
  const { user } = useAuth();
  const isStaff = user.role === "fulfillment_staff";
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [sel, setSel] = useState(null);

  const load = () => api.get("/admin/orders", { params: filter ? { status: filter } : {} }).then(({ data }) => setOrders(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const update = async (order, patch) => {
    try { const { data } = await api.put(`/admin/orders/${order.id}`, patch); setSel(data); load(); toast.success("Order updated."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const badge = (s) => <span className={`px-2 py-1 text-[0.6rem] uppercase tracking-wide2 ${s === "delivered" ? "bg-green-900/10 text-green-900" : s === "shipped" ? "bg-blue-900/10 text-blue-900" : s === "cancelled" ? "bg-destructive/10 text-destructive" : "bg-warm/40"}`}>{s}</span>;

  const printSlip = () => window.print();

  return (
    <div data-testid="admin-orders">
      <h1 className="font-heading text-4xl tracking-tight mb-2">Orders & Fulfillment</h1>
      <p className="font-body text-sm text-ink/60 mb-6">{isStaff ? "Update fulfillment status and print packing slips." : "Inspect, update and fulfill orders."}</p>

      <div className="flex gap-3 mb-6 flex-wrap">
        <button onClick={() => setFilter("")} className={`adm-btn-outline ${!filter ? "bg-ink text-linen" : ""}`}>All</button>
        {STATUSES.map((s) => <button key={s} onClick={() => setFilter(s)} data-testid={`order-filter-${s}`} className={`adm-btn-outline ${filter === s ? "bg-ink text-linen" : ""}`}>{s}</button>)}
      </div>

      <div className="adm-card p-0 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-cream"><tr className="font-body uppercase tracking-wide2 text-[0.6rem] text-ink/60">
            <th className="px-5 py-3">Order</th><th className="px-5 py-3">Customer</th><th className="px-5 py-3">Items</th>
            {!isStaff && <th className="px-5 py-3">Total</th>}<th className="px-5 py-3">Status</th><th className="px-5 py-3">Fulfillment</th><th className="px-5 py-3"></th></tr></thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-ink/5 font-body text-sm" data-testid={`order-row-${o.order_number}`}>
                <td className="px-5 py-3 font-medium">{o.order_number}</td>
                <td className="px-5 py-3 text-ink/70">{o.name}</td>
                <td className="px-5 py-3">{o.items.length}</td>
                {!isStaff && <td className="px-5 py-3">${o.total?.toFixed(2)}</td>}
                <td className="px-5 py-3">{badge(o.status)}</td>
                <td className="px-5 py-3">{badge(o.fulfillment_status || "pending")}</td>
                <td className="px-5 py-3 text-right"><button onClick={() => setSel(o)} className="icon-thin text-ink/60 hover:text-ink" data-testid={`view-order-${o.order_number}`}><Eye size={16} /></button></td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center font-body text-sm text-ink/40">No orders found.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={!!sel} onClose={() => setSel(null)} title={sel ? `Order ${sel.order_number}` : ""} wide testId="order-modal">
        {sel && (
          <div id="packing-slip" className="space-y-5">
            <div className="grid grid-cols-2 gap-4 font-body text-sm">
              <div><p className="adm-label">Customer</p><p>{sel.name}</p><p className="text-ink/60">{sel.email}</p><p className="text-ink/60">{sel.phone}</p></div>
              <div><p className="adm-label">Ship To</p><p className="text-ink/70">{[sel.shipping?.street, sel.shipping?.unit, sel.shipping?.city, sel.shipping?.state, sel.shipping?.zip, sel.shipping?.country].filter(Boolean).join(", ")}</p></div>
            </div>
            {sel.gift_note && <div className="bg-warm/30 p-3 font-body text-sm italic">Gift note: {sel.gift_note}</div>}

            <div className="border border-ink/10">
              <table className="w-full text-left font-body text-sm">
                <thead className="bg-cream"><tr className="uppercase tracking-wide2 text-[0.6rem] text-ink/60"><th className="px-4 py-2">Item</th><th className="px-4 py-2">Size</th><th className="px-4 py-2">Qty</th>{!isStaff && <th className="px-4 py-2">Price</th>}<th className="px-4 py-2">Scan</th></tr></thead>
                <tbody>{sel.items.map((it, i) => (
                  <tr key={i} className="border-t border-ink/5"><td className="px-4 py-2">{it.title}</td><td className="px-4 py-2">{it.size}</td><td className="px-4 py-2">{it.quantity}</td>{!isStaff && <td className="px-4 py-2">${(it.price * it.quantity).toFixed(2)}</td>}<td className="px-4 py-2">{(it.slug || it.product_slug) ? <img src={`${API}/qr?data=${encodeURIComponent(qrTrackUrl(`${SITE_URL}/product/${it.slug || it.product_slug}`, `packing:${sel.order_number}`))}&fill=1a1a1a&logo=1`} alt={`Scan ${it.title}`} className="w-14 h-14" data-testid={`slip-item-qr-${i}`} /> : "—"}</td></tr>
                ))}</tbody>
              </table>
            </div>

            {!isStaff && <div className="text-right font-body text-sm space-y-1">
              <p>Subtotal: ${sel.subtotal?.toFixed(2)}</p>
              {sel.discount > 0 && <p>Discount: −${sel.discount?.toFixed(2)}</p>}
              <p>Shipping: ${sel.shipping_cost?.toFixed(2)}</p>
              <p className="font-heading text-xl">Total: ${sel.total?.toFixed(2)}</p>
            </div>}

            <div className="flex items-center gap-6 border-t border-ink/10 pt-4">
              <img src={`${API}/qr?data=${encodeURIComponent(`${window.location.origin}/track/${sel.order_number}`)}`} alt="Order tracking QR" className="w-24 h-24 border border-ink/10 bg-white" data-testid="order-tracking-qr" />
              <div className="flex-1 grid grid-cols-2 gap-3 no-print">
                {!isStaff && (
                  <div><label className="adm-label">Order Status</label>
                    <select className="adm-input" value={sel.status} onChange={(e) => update(sel, { status: e.target.value })} data-testid="order-status-select">
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}
                <div><label className="adm-label">Fulfillment</label>
                  <select className="adm-input" value={sel.fulfillment_status || "pending"} onChange={(e) => update(sel, { fulfillment_status: e.target.value })} data-testid="fulfillment-status-select">
                    {FULFILL.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-span-2"><label className="adm-label">Tracking Number</label>
                  <div className="flex gap-2">
                    <input className="adm-input" defaultValue={sel.tracking_number} onBlur={(e) => e.target.value !== sel.tracking_number && update(sel, { tracking_number: e.target.value })} placeholder="Add tracking #" data-testid="tracking-input" />
                  </div>
                </div>
              </div>
            </div>
            <button onClick={printSlip} className="adm-btn w-full no-print" data-testid="print-slip-btn"><Printer size={14} /> Print Packing Slip</button>
          </div>
        )}
      </Modal>
    </div>
  );
}
